import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SxProps } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Autocomplete } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useEffect } from 'react';
import { type ReturnTypeOfUseBlockList, useBlocksList } from '../../api/blocks';

type BlockAutocompleteValue = NonNullable<ReturnTypeOfUseBlockList>[number];

export type BlockAutocompleteProps = {
  value: BlockAutocompleteValue | null;
  yearGroupId: number | undefined;
  onChange: (block: BlockAutocompleteValue | null) => void;
  sx?: SxProps<Theme> | undefined;
};

export const BlockAutocomplete = ({
  value,
  yearGroupId,
  onChange,
  sx,
}: BlockAutocompleteProps) => {
  const { t } = useTranslation(['classListManager']);
  const { data: options = [] } = useBlocksList(yearGroupId ?? 0);

  useEffect(() => {
    if (!value && options.length) {
      onChange(options[0]);
    }
  }, [options]);

  return (
    <Autocomplete
      label={t('classListManager:block')}
      value={value}
      options={options}
      optionIdKey="blockId"
      getOptionLabel={({ name, subjectGroupNamesJoined }) =>
        subjectGroupNamesJoined ? `${name}: ${subjectGroupNamesJoined}` : name
      }
      onChange={(_event, newValue) => {
        const extractedValue = Array.isArray(newValue) ? newValue[0] : newValue;
        onChange(extractedValue);
      }}
      inputProps={{
        variant: 'white-filled',
      }}
      sx={sx}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          sx: {
            width: 'auto !important',
            maxWidth: 'calc(100vw - 32px)',
          },
        },
      }}
      renderOption={(optionProps, { name, subjectGroupNamesJoined }) => (
        <Box component="li" {...optionProps}>
          <Typography noWrap component="span" variant="subtitle2">
            <Typography component="span" variant="subtitle2">
              {name}
            </Typography>
            {subjectGroupNamesJoined && (
              <Typography component="span" variant="body2">
                {`: ${subjectGroupNamesJoined}`}
              </Typography>
            )}
          </Typography>
        </Box>
      )}
    />
  );
};
